import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { 
  Users, 
  FileText, 
  Settings,
  ShieldCheck,
  BarChart3,
  UserCog,
  Wallet,
  Heart,
  LogOut,
  CreditCard,
  Building2,
  RefreshCw,
  Gift,
  MessageCircle,
  Sparkles,
  PieChart,
  Gauge,
  Award,
  UserCircle,
  Search,
  HeartHandshake,
  Briefcase,
  Bug,
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import healthWalletLogo from "@assets/generated_images/healthwallet_shield_heart_logo.png";
import { useFeatureFlags } from "@/lib/feature-flags";

const menuItems = [
  {
    titleKey: "Employer Portal",
    roles: ["employer_admin"],
    items: [
      { titleKey: "Employer Dashboard", descriptionKey: "Manage employee health benefits", url: "/employer", icon: Building2, roles: ["employer_admin"] },
      { titleKey: "Small Business Portal", descriptionKey: "Simplified health benefits management", url: "/small-business/dashboard", icon: Briefcase, roles: ["employer_admin"] },
      { titleKey: "nav.healthWallet", descriptionKey: "nav.healthWalletDesc", url: "/wallet", icon: Wallet, roles: ["employer_admin"] },
    ],
  },
  {
    titleKey: "nav.partnerPortal",
    roles: ["hospital_partner_admin"],
    items: [
      { titleKey: "nav.partnerDashboard", descriptionKey: "nav.partnerDashboardDesc", url: "/partner-portal", icon: Building2, roles: ["hospital_partner_admin"] },
      { titleKey: "nav.healthWallet", descriptionKey: "nav.healthWalletDesc", url: "/wallet", icon: Wallet, roles: ["hospital_partner_admin"] },
    ],
  },
  {
    titleKey: "nav.myWallet",
    roles: ["patient", "supporter", "driver", "dispatcher", "admin"],
    items: [
      { titleKey: "nav.healthWallet", descriptionKey: "nav.healthWalletDesc", url: "/wallet", icon: Wallet, roles: ["patient", "driver", "dispatcher", "admin"] },
      { titleKey: "nav.subscriptions", descriptionKey: "nav.subscriptionsDesc", url: "/subscriptions", icon: RefreshCw, roles: ["patient", "driver", "dispatcher", "admin"] },
      { titleKey: "nav.paymentPlans", descriptionKey: "nav.paymentPlansDesc", url: "/payment-plans", icon: CreditCard, roles: ["patient", "dispatcher", "admin"] },
      { titleKey: "nav.financialTransparency", descriptionKey: "nav.financialTransparencyDesc", url: "/financial-transparency", icon: PieChart, roles: ["patient", "driver", "dispatcher", "admin"] },
    ],
  },
  {
    titleKey: "nav.discover",
    roles: ["patient", "supporter", "driver", "dispatcher", "admin"],
    items: [
      { titleKey: "nav.communityFunds", descriptionKey: "nav.communityFundsDesc", url: "/community-funds", icon: Heart, roles: ["patient", "supporter", "driver", "dispatcher", "admin"] },
      { titleKey: "nav.conditions", descriptionKey: "nav.conditionsDesc", url: "/conditions", icon: HeartHandshake, roles: ["patient", "supporter", "driver", "dispatcher", "admin"] },
      { titleKey: "nav.discoverWallets", descriptionKey: "nav.discoverWalletsDesc", url: "/wallet/discover", icon: Search, roles: ["patient", "supporter", "driver", "dispatcher", "admin"] },
      { titleKey: "nav.referrals", descriptionKey: "nav.referralsDesc", url: "/referrals", icon: Gift, roles: ["patient", "supporter", "driver", "dispatcher", "admin"] },
    ],
  },
  {
    titleKey: "nav.community",
    roles: ["patient", "supporter", "driver", "dispatcher", "admin"],
    items: [
      { titleKey: "nav.communityHub", descriptionKey: "nav.communityHubDesc", url: "/community", icon: MessageCircle, roles: ["patient", "supporter", "driver", "dispatcher", "admin"] },
      { titleKey: "nav.engagement", descriptionKey: "nav.engagementDesc", url: "/engagement", icon: Sparkles, roles: ["patient", "supporter", "driver", "dispatcher", "admin"] },
      { titleKey: "nav.ambassador", descriptionKey: "nav.ambassadorDesc", url: "/ambassador", icon: Award, roles: ["patient", "supporter", "driver", "dispatcher", "admin"] },
    ],
  },
  {
    titleKey: "nav.myAccount",
    roles: ["patient", "supporter", "driver", "dispatcher", "admin"],
    items: [
      { titleKey: "nav.myProfile", descriptionKey: "nav.myProfileDesc", url: "/profile", icon: UserCircle, roles: ["patient", "supporter"] },
      { titleKey: "nav.settings", descriptionKey: "nav.settingsDesc", url: "/settings", icon: Settings, roles: ["driver", "dispatcher", "admin"] },
    ],
  },
  {
    titleKey: "nav.compliance",
    roles: ["admin"],
    items: [
      { titleKey: "nav.auditLogs", descriptionKey: "nav.auditLogsDesc", url: "/audit", icon: ShieldCheck, roles: ["admin"] },
      { titleKey: "nav.reports", descriptionKey: "nav.reportsDesc", url: "/reports", icon: FileText, roles: ["admin"] },
      { titleKey: "nav.analytics", descriptionKey: "nav.analyticsDesc", url: "/analytics", icon: BarChart3, roles: ["admin"] },
      { titleKey: "nav.hospitalPartners", descriptionKey: "nav.hospitalPartnersDesc", url: "/admin/hospital-partners", icon: Building2, roles: ["admin"] },
    ],
  },
  {
    titleKey: "nav.admin",
    roles: ["admin"],
    items: [
      { titleKey: "nav.controlCenter", descriptionKey: "nav.controlCenterDesc", url: "/admin-control", icon: Gauge, roles: ["admin"] },
      { titleKey: "nav.userManagement", descriptionKey: "nav.userManagementDesc", url: "/admin", icon: UserCog, roles: ["admin"] },
      { titleKey: "nav.errorTracker", descriptionKey: "nav.errorTrackerDesc", url: "/admin/errors", icon: Bug, roles: ["admin"] },
    ],
  },
];

export function AppSidebar() {
  const [location] = useLocation();
  const { user, logoutMutation } = useAuth();
  const { t } = useTranslation();
  const { disabledSidebarUrls } = useFeatureFlags();

  const { data: pendingInvitations } = useQuery<any[]>({
    queryKey: ["/api/user/pending-invitations"],
    enabled: !!user,
    refetchInterval: 60000,
  });
  const pendingCount = pendingInvitations?.length || 0;

  const { data: errorStats } = useQuery<{ unresolvedCount: number }>({
    queryKey: ["/api/admin/errors/stats"],
    enabled: !!user && user.role === "admin",
    refetchInterval: 60000,
  });
  const unresolvedErrorCount = errorStats?.unresolvedCount || 0;

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  return (
    <Sidebar>
      <SidebarHeader className="border-b border-sidebar-border p-4">
        <Link href="/home" data-testid="link-home" className="flex items-center gap-3 cursor-pointer">
          <img 
            src={healthWalletLogo} 
            alt="HealthWallet Logo" 
            className="h-10 w-10 object-contain"
            width="1024"
            height="1024"
            data-testid="img-sidebar-logo"
          />
          <div>
            <div className="font-semibold text-sidebar-foreground">{t('app.name')}</div>
            <div className="text-xs text-sidebar-foreground/70 max-w-[140px] leading-tight">{t('app.tagline')}</div>
          </div>
        </Link>
      </SidebarHeader>
      <SidebarContent>
        {menuItems
          .filter(section => user && section.roles.includes(user.role))
          .map((section) => {
            const filteredItems = section.items.filter(item => user && item.roles.includes(user.role) && !disabledSidebarUrls.has(item.url));
            
            if (filteredItems.length === 0) return null;
            
            return (
              <SidebarGroup key={section.titleKey}>
                <SidebarGroupLabel>{t(section.titleKey)}</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {filteredItems.map((item) => {
                      const isActive = location === item.url || (item.url !== "/" && location.startsWith(item.url + "/"));
                      const translatedTitle = t(item.titleKey) || item.titleKey;
                      const testId = item.url.replace(/\//g, '-').replace(/^-/, '') || 'home';
                      const showInviteBadge = item.url === "/wallet" && pendingCount > 0;
                      const showErrorBadge = item.url === "/admin/errors" && unresolvedErrorCount > 0;
                      return (
                        <SidebarMenuItem key={item.titleKey}>
                          <SidebarMenuButton
                            asChild
                            isActive={isActive}
                            data-testid={`link-${testId}`}
                          >
                            <Link href={item.url} className="min-h-[44px] flex items-center">
                              <item.icon className="h-4 w-4" />
                              <span>{translatedTitle}</span>
                              {showInviteBadge && (
                                <Badge variant="destructive" className="ml-auto text-[10px] px-1.5 py-0" data-testid="badge-pending-invitations-sidebar">
                                  {pendingCount}
                                </Badge>
                              )}
                              {showErrorBadge && (
                                <Badge variant="destructive" className="ml-auto text-[10px] px-1.5 py-0" data-testid="badge-unresolved-errors-sidebar">
                                  {unresolvedErrorCount}
                                </Badge>
                              )}
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            );
          })}
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border p-4">
        {user && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-medium text-primary">
                  {user.firstName?.[0]}{user.lastName?.[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {user.firstName} {user.lastName}
                </div>
                <div className="text-xs text-muted-foreground capitalize">
                  {user.role}
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="default"
              className="w-full min-h-[44px]"
              onClick={handleLogout}
              disabled={logoutMutation.isPending}
              data-testid="button-logout"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {logoutMutation.isPending ? t('common.loading') : t('auth.logout')}
            </Button>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
